import { getCountryCode } from "../../Wallets/utils";

type AccountId = string | number;

type AccountLike = {
  id?: AccountId | null;
  accountNumber?: string | null;
};

type WireBeneficiary = {
  bankName?: string;
  accountNumber?: string | null;
  beneficiaryName?: string;
  country?: string;
  swiftCode?: string;
  iban?: string;
  bankCode?: string;
};

export function getDefinedAccountId(
  id?: AccountId | null,
): AccountId | undefined {
  if (typeof id === "number") {
    return id;
  }

  if (typeof id === "string" && id !== "") {
    return id;
  }

  return undefined;
}

export function findMerchantAccountIdByNumber(
  accounts: AccountLike[] | null | undefined,
  accountNumber?: string | null,
): AccountId | undefined {
  if (!accountNumber || !Array.isArray(accounts)) {
    return undefined;
  }

  const match = accounts.find(
    (bank) => bank?.accountNumber === accountNumber,
  );

  return getDefinedAccountId(match?.id);
}

export function buildInternationalMerchantAccountPayload({
  beneficiary,
  userId,
  businessId,
  currency,
}: {
  beneficiary?: WireBeneficiary;
  userId?: string | null;
  businessId?: number | null;
  currency?: string;
}) {
  return {
    bankName: beneficiary?.bankName,
    accountNumber: beneficiary?.accountNumber,
    accountName: beneficiary?.beneficiaryName,
    country: getCountryCode(beneficiary?.country ?? "") ?? beneficiary?.country,
    currency,
    owner: userId,
    businessId,
    swiftCode: beneficiary?.swiftCode,
    iban: beneficiary?.iban,
    bankCode: beneficiary?.bankCode ?? "",
  };
}

export async function resolveSettlementPayoutAccountId({
  existingAccounts,
  beneficiaryAccountNumber,
  createAccount,
  refreshAccounts,
}: {
  existingAccounts: AccountLike[] | null | undefined;
  beneficiaryAccountNumber?: string | null;
  createAccount: () => Promise<{ id?: AccountId | null } | null | undefined>;
  refreshAccounts?: () => Promise<AccountLike[] | null | undefined>;
}): Promise<AccountId> {
  const existingId = findMerchantAccountIdByNumber(
    existingAccounts,
    beneficiaryAccountNumber,
  );

  if (existingId !== undefined) {
    return existingId;
  }

  if (!beneficiaryAccountNumber) {
    throw new Error("Unable to resolve a bank account for this payout");
  }

  const created = await createAccount();
  let accountId = getDefinedAccountId(created?.id);

  if (accountId === undefined && refreshAccounts) {
    const refreshed = await refreshAccounts();
    accountId = findMerchantAccountIdByNumber(
      refreshed,
      beneficiaryAccountNumber,
    );
  }

  if (accountId === undefined) {
    throw new Error("Unable to resolve a bank account for this payout");
  }

  return accountId;
}
